import {
  Component,
  OnInit,
  ElementRef,
  HostListener,
  forwardRef,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, Subject, combineLatest } from 'rxjs';
import { takeUntil, map, take } from 'rxjs/operators';
import { Select, Store } from '@ngxs/store';
import { AccordionPanelComponent, AccordionModule } from 'ngx-bootstrap/accordion';
import { chain, get } from 'lodash';
import MicroModal from 'micromodal';
import {
  ComponentConfig,
  Configuration,
  ConfigurationState,
  NotificationService,
  Notification,
  NotificationRecipient,
  UserService, LaunchDarklyService,
  emailFormatValidator,
  noSpecialCharsEmailValidator
} from 'remarketing-angular-library';
import { tenantClass } from '../../util/state-selectors';
import { AnalyticsService, SaComponent } from 'smartauction-frontend-micro-library';
import { CheckUnsavedForms } from '../../interfaces/check-unsaved-forms';
import { NotificationsService } from '../../services/notifications/notifications.service';
import { MobilePhonePipe } from '../../pipes/mobile-phone/mobile-phone.pipe';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { SaveChangesModalComponent } from '../save-changes-modal/save-changes-modal.component';
import { SuccessMessageComponent } from '../success-message/success-message.component';
import { ErrorMessageComponent } from '../error-message/error-message.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { AlertTrackingConfig, AlertTrackingService } from '../../services/alert-tracking/alert-tracking.service';

const SEND_TYPE_TEXT = 'Text';

const enum Mode {
  BUY = 'buy',
  EMAIL = 'email',
  MOBILE_PHONE = 'mobilePhone',
  SELL = 'sell'
}

const enum Category {
  BUY = 'Buy',
  SELL = 'Sell'
}

@Component({
    selector: 'app-alerts-alerts',
    templateUrl: './alerts-alerts.component.html',
    styleUrls: ['./alerts-alerts.component.scss'],
    providers: [
      {
        provide: CheckUnsavedForms,
        useExisting: forwardRef(() => AlertsAlertsComponent)
      }
    ],
    imports: [TranslocoDirective, NgClass, AccordionModule, ErrorMessageComponent, SuccessMessageComponent, SaveChangesModalComponent, AsyncPipe]
})
  export class AlertsAlertsComponent extends SaComponent implements OnInit, CheckUnsavedForms, AfterViewInit {
  @Select(ConfigurationState.configuration) private configuration$: Observable<Configuration>;
  @Select(ConfigurationState.coreComponentConfig('profile-settings')) profileConfig$: Observable<ComponentConfig>;
  @Select(tenantClass) tenantClass$: Observable<string>;
  @Output() closeParentAccordion = new EventEmitter();

  activeChildRoute: string;
  readonly activeClass = 'profile-nav__link--active';

  private cancelEditing$: Subject<boolean> = new Subject<boolean>();

  tenantName$: Observable<string>;
  tenantUuid$: Observable<string>;
  tenantName: string;
  tenantUuid: string;
  buyerShortCode$: Observable<string>;
  buyerShortCode: string;
  sellerShortCode$: Observable<string>;
  sellerShortCode: string;
  privacyURL$: Observable<string>;
  privacyURL: string;
  buyAlerts$: Observable<Array<Notification>>;
  sellAlerts$: Observable<Array<Notification>>;
  editMode: string;
  mobileNotifTenantText: string;
  termsAndConditionsLink: string;
  factorPhone: string;
  factorEnrolled: boolean;
  contactForm: UntypedFormGroup;
  message: string;
  userProfile;
  hasSellerEligibility: boolean;
  recipient: NotificationRecipient;
  componentUuid: string;
  routerState: any;
  toggleAccordions = false;
  editModeEmail = false;
  editModeMobilePhone = false;
  phoneAdded = false;
  showError = false;
  showSuccess = false;
  showBuyError = false;
  showBuySuccess = false;
  showSellError = false;
  showSellSuccess = false;
  buyIsOpen = false;
  sellIsOpen = false;
  myAlertsandNotifications: string;
  isMobile = window.innerWidth < 768;
  modalId = 'user-contact__save-changes-modal';
  optedNotifications: Array<Notification> = [];
  optedNotificationsOriginal: Array<Notification> = [];
  visibilityFlags: any = [];
  sellNotificationType: Array<string> = [];
  buyNotificationType: Array<string> = [];

  @ViewChild('sellAccordion') sellAccordion: AccordionPanelComponent;
  @ViewChild('buyAccordion') buyAccordion: AccordionPanelComponent;
  @ViewChild('pageHeading') pageHeading: ElementRef;
  @ViewChild('accordionWrapper') accordionWrapper: ElementRef;

  get isDrawerOpen(): boolean {
    return this.editModeEmail || this.editModeMobilePhone;
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private analyticsService: AnalyticsService,
    private notificationService: NotificationService,
    private notificationsService: NotificationsService,
    private alertTrackingService: AlertTrackingService,
    private store: Store,
    private readonly router: Router,
    private readonly mobilePhonePipe: MobilePhonePipe,
    private readonly activatedRoute: ActivatedRoute,
    private readonly userService: UserService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly translocoService: TranslocoService
  ) {
    super();
  }

  ngOnInit(): void {
    this.getTranslations();
    this.notificationsService.visibilityFlags$.pipe(takeUntil(this.destroyed$)).subscribe(flags => {
      this.visibilityFlags = flags;
    });
    this.buyAlerts$ = this.notificationsService.buyAlerts$.asObservable();
    this.sellAlerts$ = this.notificationsService.sellAlerts$.asObservable();

    this.store
      .select(state => state.user.userProfile)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(profile => {
        this.userProfile = profile;
      });

    this.tenantName$ = this.configuration$.pipe(map((configuration: Configuration) => configuration.tenantName));
    this.tenantName$.pipe(takeUntil(this.destroyed$)).subscribe(tenantName => (this.tenantName = tenantName));

    this.tenantUuid$ = this.configuration$.pipe(map((configuration: Configuration) => configuration.tenantUuid));
    this.tenantUuid$.pipe(takeUntil(this.destroyed$)).subscribe(tenantUuid => (this.tenantUuid = tenantUuid));

    this.buyerShortCode$ = this.profileConfig$.pipe(
      map(profileConfig => get(profileConfig, 'labels.buyer-short-code', undefined))
    );
    this.buyerShortCode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(buyerShortCode => (this.buyerShortCode = buyerShortCode));
    this.sellerShortCode$ = this.profileConfig$.pipe(
      map(profileConfig => get(profileConfig, 'labels.seller-short-code', undefined))
    );
    this.sellerShortCode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(sellerShortCode => (this.sellerShortCode = sellerShortCode));

    this.privacyURL$ = this.profileConfig$.pipe(
      map(profileConfig => get(profileConfig, 'labels.privacy-policy-url', undefined))
    );
    this.privacyURL$.pipe(takeUntil(this.destroyed$)).subscribe(privacyURL => (this.privacyURL = privacyURL));
    this.contactForm = this.formBuilder.group({
      emailAddress: [
        this.userProfile.email,
        [Validators.required, emailFormatValidator(), noSpecialCharsEmailValidator()]
      ],
      phoneNumber: [
        this.mobilePhonePipe.transform(this.userProfile.mobilePhone),
        [Validators.minLength(10), Validators.maxLength(12)]
      ],
      mobileConsent: [false, [Validators.required]]
    });

    this.analyticsService.trackPageLoad({
      page: {
        pageName: `${this.analyticsService.digitalData.page.category.site}:Profile Settings:My Information:Contact`,
        section: 'Profile Settings',
        subsection: 'My Information',
        title: 'Contact'
      }
    });

    this.store
      .select(state => state.configuration.configuration)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(configuration => {
        this.mobileNotifTenantText = get(configuration, 'tenantFullName', null);
        this.termsAndConditionsLink = get(configuration, 'smsTermsUrl', null);
      });

    this.store
      .select(state => state.org.organization)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(organization => {
        this.hasSellerEligibility = organization?.organizationProfile.eligibleToSell;
      });

    this.getRecipient(recipient => {
      if (this.buyAccordion) {
        this.buyAccordion.isDisabled = false;
      }
      if (this.sellAccordion) {
        this.sellAccordion.isDisabled = false;
      }

      this.contactForm.controls.emailAddress.setValue(recipient.email);
      this.contactForm.controls.phoneNumber.setValue(this.mobilePhonePipe.transform(recipient.mobilePhone));
      this.contactForm.controls.mobileConsent.setValue(!!recipient.mobileConsent);

      if (this.toggleAccordions) {
        this.toggleBuyAccordion();
        this.toggleSellAccordion();
        this.scrollIntoViewAlertsHeader();
      }
    });
  }

  getUserId() {
    return get(this.userProfile, 'userId', '');
  }

  get featureMobileNotif(): boolean {
    return this.launchDarklyService.getFeatureFlag('login-mobileNotificationTextEnabled', false);
  }

  get mobileConsent(): boolean {
    return this.recipient && this.recipient.mobileConsent ? this.recipient.mobileConsent : false;
  }

  get requireMobileConsent(): boolean {
    return this.featureMobileNotif && !this.mobileConsent;
  }

  get isContactFormPristine(): boolean {
    return chain(this.contactForm.controls)
      .every('pristine')
      .value();
  }

  get showSellerAlerts() {
    return this.userService.isSellerRole() && this.hasSellerEligibility;
  }

  focusMessage(id) {
    setTimeout(() => {
      const message = document.getElementById(id) as HTMLElement;
      if (message) {
        message.focus();
      }
    }, 100);
  }

  ngAfterViewInit(): void {
    if (this.routerState && this.routerState.focus) {
      this.pageHeading.nativeElement.focus();
    }
    this.setAccordionRoles();
  }

  setAccordionRoles() {
    const accordion = this.accordionWrapper.nativeElement.querySelector('.alert-settings');
    const navItems = this.accordionWrapper.nativeElement.querySelectorAll('.child-accordion .panel');

    accordion.removeAttribute('role');

    navItems.forEach(item => {
      item.setAttribute('role', 'list');
      item.children[0].setAttribute('role', 'listitem');
      item.children[1].setAttribute('role', 'listitem');
    });
  }

  hasUnsavedForms(): boolean {
    if (this.isDrawerOpen) {
      return !this.isContactFormPristine;
    } else {
      return this.areNotEqual(this.optedNotifications, this.optedNotificationsOriginal);
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    event.preventDefault();
    if (this.hasUnsavedForms()) {
      event.returnValue = true;
    }
  }

  showAlert() {
    this.openMicroModal(this.modalId);
  }

  cancelEditing() {
    if (this.editMode === Mode.BUY) {
      this.resetNotifications(Category.BUY);
      this.focusAlertsHeader();
      this.buyIsOpen = false;
      this.buyAccordion.isOpen = false;
      this.buyAccordion.isDisabled = false;
    } else if (this.editMode === Mode.SELL) {
      this.resetNotifications(Category.SELL);
      this.focusAlertsHeader();
      this.sellIsOpen = false;
      this.sellAccordion.isOpen = false;
      this.sellAccordion.isDisabled = false;
    } else if (this.editMode === Mode.MOBILE_PHONE) {
      this.contactForm.controls.phoneNumber.setValue(this.mobilePhonePipe.transform(this.recipient.mobilePhone));
      this.contactForm.controls.phoneNumber.markAsPristine();
      this.editModeMobilePhone = false;
    } else if (this.editMode === Mode.EMAIL) {
      this.contactForm.controls.emailAddress.setValue(this.recipient.email);
      this.contactForm.controls.emailAddress.markAsPristine();
      this.editModeEmail = false;
    } else {
      this.focusAlertsHeader();
      this.resetNotifications(Category.SELL);
      this.resetNotifications(Category.BUY);
      this.buyIsOpen = false;
      this.sellIsOpen = false;
      this.buyAccordion.isOpen = false;
      this.buyAccordion.isDisabled = false;
      this.sellAccordion.isOpen = false;
      this.sellAccordion.isDisabled = false;
    }

    this.cancelEditing$.next(true);
    this.cancelEditing$.complete();
    this.closeParentAccordion.emit();
  }

  resetNotifications(category) {
    this.optedNotifications = this.optedNotificationsOriginal.filter(n => {
      if (n.category === category) {
        return this.optedNotificationsOriginal.find(ogN => ogN.type === n.type);
      } else {
        return true;
      }
    });
  }

  continueEditing() {
    this.cancelEditing$.next(false);
    this.cancelEditing$.complete();
  }

  cancelBuy() {
    this.editMode = Mode.BUY;
    if (this.areNotEqual(this.optedNotifications, this.optedNotificationsOriginal, Category.BUY)) {
      this.showNavigationAlert();
    } else {
      this.focusAlertsHeader();
      this.buyIsOpen = false;
      this.buyAccordion.isOpen = false;
      this.buyAccordion.isDisabled = false;
    }
  }

  cancelSell() {
    this.editMode = Mode.SELL;
    if (this.areNotEqual(this.optedNotifications, this.optedNotificationsOriginal, Category.SELL)) {
      this.showNavigationAlert();
    } else {
      this.focusAlertsHeader();
      this.sellIsOpen = false;
      this.sellAccordion.isOpen = false;
      this.sellAccordion.isDisabled = false;
    }
  }

  scrollIntoViewAlertsHeader() {
    setTimeout(() => {
      const header = document.getElementById('alerts-header');
      if (header) {
        header.scrollIntoView();
      }
    }, 200);
  }

  updateNotificationBuy(e, notification) {
    this.editMode = Mode.BUY;
    this.updateNotification(e, notification);
    this.buyAccordion.isDisabled = this.areNotEqual(
      this.optedNotifications,
      this.optedNotificationsOriginal,
      Category.BUY
    );
  }

  updateNotificationSell(e, notification) {
    this.editMode = Mode.SELL;
    this.updateNotification(e, notification);
    this.sellAccordion.isDisabled = this.areNotEqual(
      this.optedNotifications,
      this.optedNotificationsOriginal,
      Category.SELL
    );
  }

  updateNotification(e, notification) {
    if (e.target.checked) {
      this.optedNotifications.push(notification);
    } else {
      const i = this.optedNotifications.findIndex(n => n.type === notification.type);
      this.optedNotifications.splice(i, 1);
    }
  }

  saveBuyNotifs() {
    this.showBuyError = false;
    this.showBuySuccess = false;
    this.saveNotifs(
      () => {
        this.showBuySuccess = true;
        this.focusMessage('buy-success-message');
        this.buyAccordion.isDisabled = false;
      },
      () => {
        this.showBuyError = true;
        this.focusMessage('buy-error-message');
      },
      'Buy'
    );
  }

  saveSellNotifs() {
    this.showSellError = false;
    this.showSellSuccess = false;
    this.saveNotifs(
      () => {
        this.focusMessage('sell-success-message');
        this.sellAccordion.isDisabled = false;
        this.showSellSuccess = true;
      },
      () => {
        this.showSellError = true;
        this.focusMessage('sell-error-message');
      },
      'Sell'
    );
  }

  saveNotifs(callback, error, accordion) {
    const payload = {
      ...this.recipient,
      optedNotifications: this.optedNotifications,
      tenantUuid: this.tenantUuid
    };

    this.trackNotifications(accordion);

    this.updateRecipient(
      payload,
      recipient => {
        if (recipient.optedNotifications) {
          this.optedNotifications = recipient.optedNotifications;
          this.optedNotificationsOriginal = JSON.parse(JSON.stringify(recipient.optedNotifications));
        }
        this.trackAlerts(true, accordion);
        callback();
      },
      () => {
        this.trackAlerts(false, accordion);
        error();
      }
    );
  }

  showBuyAndSellOptions() {
    this.scrollIntoViewAlertsHeader();
    this.sellIsOpen = true;
    this.buyIsOpen = true;
  }

  onSuccessGoTo() {
    this.sellIsOpen = true;
    this.buyIsOpen = true;
  }

  trackNotifications(accordion) {
    this.analyticsService.track('customEvent', {
      name: 'MOA Tracking and Analytics',
      type: 'customEvent',
      category: 'UserInteraction:CustomEvent',
      attr: {
        optedNotifications: this.printNotifs(this.optedNotifications, accordion)
      }
    });
  }

  getAlertDescription(alert: Notification): string {
    return this.translocoService.translateObject('notifications.' + alert.type);
  }

  printNotifs(notifications: Array<any>, accordion) {
    const notifs: Array<String> = [];
    for (const notification of notifications) {
      if (notification.category == accordion) {
        const category = notification.category;
        const sendType = notification.sendType;
        const description = notification.description;
        const finalString = 'Category: ' + category + ', Alert Type: ' + sendType + ', Selected Alert: ' + description;
        notifs.push(finalString);
      }
    }
    return notifs;
  }

  toggleBuyAccordion() {
    if (!this.buyAccordion.isDisabled) {
      setTimeout(() => {
        this.buyIsOpen = !this.buyIsOpen;
      }, 100);
    }
    if (this.areNotEqual(this.optedNotifications, this.optedNotificationsOriginal, Category.BUY)) {
      this.showNavigationAlert();
    }
  }

  toggleSellAccordion() {
    if (!this.sellAccordion.isDisabled) {
      setTimeout(() => {
        this.sellIsOpen = !this.sellIsOpen;
      }, 100);
    }
    if (this.areNotEqual(this.optedNotifications, this.optedNotificationsOriginal, Category.SELL)) {
      this.showNavigationAlert();
    }
  }

  checkVisibility(alert) {
    for (const key in this.visibilityFlags) {
      if (alert && alert.description && key.toLowerCase() === alert.description.toLowerCase()) {
        return this.visibilityFlags[key].visible;
      }
    }
  }

  checked(type: string): boolean {
    const notification: Notification = this.optedNotifications.find(n => n.type === type);

    if (this.featureMobileNotif && notification && SEND_TYPE_TEXT === notification.sendType) {
      return this.mobileConsent;
    }

    return !!notification;
  }

  hasAlertType(alerts: any[], alertType: string): boolean {
    let hasAlerts = false;
    alerts.forEach(alert => {
      if (alert.sendType === alertType && this.checkVisibility(alert)) {
        hasAlerts = true;
      }
    });
    return hasAlerts;
  }

  private trackAlerts(isSuccess: boolean, accordion): void {
    combineLatest([this.buyAlerts$.pipe(take(1)), this.sellAlerts$.pipe(take(1))]).subscribe(
      ([buyAlerts, sellAlerts]) => {
        const config = this.alertTrackingService.createConfigForAccordion(
          accordion,
          buyAlerts,
          sellAlerts,
          alert => this.checkVisibility(alert),
          alert => this.getAlertDescription(alert),
          type => this.checked(type)
        );
        this.alertTrackingService.trackAlertPreferences(config, isSuccess);
      }
    );
  }

  isOpenChange(e, type) {
    if (e) {
      setTimeout(() => {
        const table = document.getElementById(type + '_table') as HTMLElement;
        if (table) {
          table.focus();
        }
      }, 200);
    }
  }

  areNotEqual(array, ogArray, category?) {
    let arr1;
    let arr2;
    if (category) {
      arr1 = array
        .concat()
        .filter(n => n.category === category)
        .sort();
      arr2 = ogArray
        .concat()
        .filter(n => n.category === category)
        .sort();
    } else {
      arr1 = array.concat().sort();
      arr2 = ogArray.concat().sort();
    }

    if (arr1.length !== arr2.length) {
      return true;
    }

    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i].type !== arr2[i].type) {
        return true;
      }
    }

    return false;
  }

  focusAlertsHeader() {
    setTimeout(() => {
      const header = document.getElementById('alerts-header') as HTMLElement;
      if (header) {
        header.focus();
      }
    }, 0);
  }

  showNavigationAlert(): Observable<boolean> {
    this.openMicroModal(this.modalId);
    this.cancelEditing$ = new Subject<boolean>();
    return this.cancelEditing$.asObservable();
  }

  openMicroModal(modalId: string) {
    MicroModal.show(modalId, {
      disableScroll: true
    });
    const heading = document.querySelector(`#${modalId} h1`) as HTMLElement;
    if (heading) {
      heading.focus();
    }
  }

  getTranslations() {
    this.translocoService
      .selectTranslateObject('')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.setMessage();
      });
  }

  setMessage() {
    this.translocoService
      .selectTranslateObject('profileManagement.userContact')
      .pipe(take(1))
      .subscribe(translate => {
        this.myAlertsandNotifications = ' ' + translate.textSaveChanges;
        this.message = this.myAlertsandNotifications;
      });
  }

  navigateTo(route) {
    setTimeout(() => this.router.navigateByUrl(route), 0);
  }

  private getRecipient(callback?) {
    return this.notificationService
      .getRecipient(this.userProfile.userUuid)
      .pipe(take(1))
      .subscribe(recipient => {
        this.recipient = recipient;
        this.optedNotifications = recipient.optedNotifications;
        this.optedNotificationsOriginal = JSON.parse(JSON.stringify(recipient.optedNotifications));

        if (callback) {
          callback(recipient);
        }
      });
  }

  isAlertsPageEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('profile-alertsPageEnabled', false);
  }

  private updateRecipient(payload, callback?, error?) {
    return this.notificationService
      .updateRecipient(this.userProfile.userUuid, payload)
      .pipe(take(1))
      .subscribe(
        recipient => {
          this.recipient = recipient;
          if (callback) {
            callback(recipient);
          }
        },
        () => {
          if (error) {
            error();
          }
        }
      );
  }
}

